import { useReducedMotion } from "./hooks/useReducedMotion.js";

/**
 * JavaScript mirrors of the timing tokens in `tokens/tokens.json`.
 *
 * CSS owns visual timing. These numbers exist only for lifecycle work that CSS
 * cannot do, such as retaining an exiting overlay until its transition ends.
 */
export const MOTION_DURATION_MS = {
  fast: 120,
  state: 180,
  overlay: 240,
  layout: 300,
  camera: 520,
} as const;

export type MotionPhase = "opening" | "open" | "closing" | "closed";

/**
 * Transition for dnd-kit's `useSortable`, so a dragged row settles on the
 * design system's clock instead of the library default `200ms ease`
 * (ADR-0091). Consumed by `SortableItem` and `SortableCard`.
 *
 * dnd-kit writes the transition inline in JavaScript, so the
 * `prefers-reduced-motion` block in `theme.css` cannot collapse it: a caller
 * reads {@link useReducedMotion} and passes `transition: null` instead of this,
 * which is what removes the settle outright.
 */
export const DND_TRANSITION = {
  duration: MOTION_DURATION_MS.state,
  easing: "cubic-bezier(0.2, 0, 0, 1)",
} as const;

/**
 * Duration in ms for JS-driven camera work — ReactFlow `fitBounds`,
 * `setCenter`, `zoomTo` — and **0 when the viewer asks for reduced motion**.
 *
 * This is not a nicety. The `prefers-reduced-motion` block in `theme.css`
 * collapses CSS transitions only; a camera flight is animated in JavaScript by
 * the library and the media query never reaches it. Before this hook, canvas
 * framing carried nine different hand-written numbers and moved the viewport
 * for everyone regardless of the setting.
 *
 * `framing` re-frames the whole canvas (fit-all, reveal a node, jump to a
 * phase), `step` is a discrete zoom in or out, `nudge` is a small correction.
 */
export function useCameraDuration(
  intent: "framing" | "step" | "nudge" = "framing",
): number {
  const reducedMotion = useReducedMotion();
  if (reducedMotion) return 0;
  if (intent === "step") return MOTION_DURATION_MS.overlay;
  if (intent === "nudge") return MOTION_DURATION_MS.state;
  return MOTION_DURATION_MS.camera;
}
